import { useState, useEffect } from "react";

import { Grid, makeStyles, Typography } from "@material-ui/core";
import _debounce from "lodash/debounce";

import { api } from "../../../../services";
import { COLORS } from "../../../../theme/colors";
import { Slider } from "../../../../components/base/Slider";
import { TextButton } from "../../../../components/base/Button";
import { ShaDowBox } from "../../../../components/boxes/ShadowBox";
import { PortfolioPowerRevenueCharges } from "./PortfolioPowerRevenueCharges";

const useStyles = makeStyles((theme) => ({
  box: {
    padding: `${theme.spacing(3.5)}px ${theme.spacing(3)}px`,
  },
  row: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(5),
  },
  subTitle: {
    color: COLORS.darks[0][50],
    fontStyle: "italic",
  },
}));

export const UnrealizedNewPowerAssets = () => {
  const classes = useStyles();
  const [data, setData] = useState();
  const [currentChartData, setCurrentChartData] = useState([]);
  const [estimationChartData, setEstimationChartData] = useState([]);
  const [currentAddingPowerRevenues, setCurrentAddingPowerRevenues] = useState();
  const [currentReducingUnrealizedRevenues, setCurrentReducingUnrealizedRevenues] = useState();
  const [estimationAddingPowerRevenues, setEstimationAddingPowerRevenues] = useState();
  const [estimationReducingUnrealizedRevenues, setEstimationReducingUnrealizedRevenues] =
    useState();

  useEffect(() => {
    const handleNewPowerAssetChange = (url, queryParams) => {
      api.get(url).then((resp) => {
        const rawData = resp.data;
        if (!data) {
          setData({
            default_percent_solar: rawData.percent_solar,
            default_percent_batteries: rawData.percent_batteries,
            default_percent_ev: rawData.percent_ev,
            default_percent_new: rawData.percent_new,
          });
        }
        let new_power_revenues = rawData.new_power_revenues;

        let power_revenues =
          new_power_revenues.batteries +
          new_power_revenues.solar +
          new_power_revenues.ev +
          new_power_revenues.new;

        setCurrentAddingPowerRevenues(power_revenues);
        setCurrentReducingUnrealizedRevenues(
          rawData.revised_unrealized_batteries +
            rawData.revised_unrealized_solar +
            rawData.revised_unrealized_ev +
            rawData.revised_unrealized_new -
            (rawData.today_batteries + rawData.today_solar + rawData.today_ev + rawData.today_new)
        );
        setCurrentChartData([
          {
            battery: rawData.today_batteries,
            solar: rawData.today_solar,
            ev: rawData.today_ev,
            new_power_asset: rawData.today_new,
          },
          {
            battery: rawData.revised_unrealized_batteries,
            solar: rawData.revised_unrealized_solar,
            ev: rawData.revised_unrealized_solar,
            new_power_asset: rawData.revised_unrealized_new,
          },
          {
            new_power_revenues: power_revenues,
          },
        ]);
      });

      api.get(`${url}${queryParams ? "&" : "?"}years=20`).then((resp) => {
        const rawData = resp.data;

        let new_power_revenues = rawData.new_power_revenues;
        let power_revenues =
          new_power_revenues.batteries +
          new_power_revenues.solar +
          new_power_revenues.ev +
          new_power_revenues.new;
        setEstimationAddingPowerRevenues(power_revenues);
        setEstimationReducingUnrealizedRevenues(
          rawData.revised_unrealized_batteries +
            rawData.revised_unrealized_solar +
            rawData.revised_unrealized_ev +
            rawData.revised_unrealized_new -
            (rawData.today_batteries + rawData.today_solar + rawData.today_ev + rawData.today_new)
        );
        setEstimationChartData([
          {
            battery: rawData.today_batteries,
            solar: rawData.today_solar,
            ev: rawData.today_ev,
            new_power_asset: rawData.today_new,
          },
          {
            battery: rawData.revised_unrealized_batteries,
            solar: rawData.revised_unrealized_solar,
            ev: rawData.revised_unrealized_ev,
            new_power_asset: rawData.revised_unrealized_new,
          },
          {
            new_power_revenues: power_revenues,
          },
        ]);
      });
    };

    let url = "/praas/v1/unrealized/assets/";
    let queryParams = [];
    if (data) {
      if ("percent_solar" in data) {
        let ps = data.percent_solar;
        queryParams.push(`solar=${ps}`);
      }
      if ("percent_batteries" in data) {
        let pb = data.percent_batteries;
        queryParams.push(`batteries=${pb}`);
      }
      if ("percent_ev" in data) {
        let pe = data.percent_ev;
        queryParams.push(`ev=${pe}`);
      }
      if ("percent_new" in data) {
        let pn = data.percent_new;
        queryParams.push(`new=${pn}`);
      }
    }

    if (queryParams) url += `?${queryParams.join("&")}`;
    handleNewPowerAssetChange(url, queryParams);
  }, [data]);

  const debouncedChange = _debounce((key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, 400);

  const handleChange = (key, value) => {
    debouncedChange(key, value);
  };

  const handleClick = () => {
    setData((prev) => ({
      ...prev,
      percent_solar: data.default_percent_solar,
      percent_batteries: data.default_percent_batteries,
      percent_ev: data.default_percent_ev,
      percent_new: data.default_percent_new,
    }));
  };

  return (
    <>
      {data ? (
        <ShaDowBox style={{ marginBottom: 30 }}>
          <Grid container>
            <Grid
              className={classes.box}
              item
              xs={12}
              sm={4}
              style={{ borderRight: `1px solid ${COLORS.darks[2]}` }}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                <Typography variant="h4">Unrealized New Power Aseets </Typography>
                <TextButton
                  text="Revert"
                  textStyle={{ fontWeight: 400, fontSize: 12 }}
                  onClick={handleClick}
                />
              </Grid>

              <Typography className={classes.subTitle}>
                Drag parameters to change revenues
              </Typography>

              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography variant="body2">Installed batteries</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color={COLORS.majorelleBlue}
                    value={data.percent_batteries || data.default_percent_batteries}
                    defaultValue={data.default_percent_batteries}
                    unit="kWh"
                    onChange={(e, newValue) => handleChange("percent_batteries", newValue)}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography variant="body2">Installed EV Chargers</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color="#63B9C4"
                    value={data.percent_ev || data.default_percent_ev}
                    defaultValue={data.default_percent_ev}
                    unit="kW"
                    onChange={(e, newValue) => handleChange("percent_ev", newValue)}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography variant="body2">Installed solar sys.</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color={COLORS.lightYellow}
                    value={data.percent_solar || data.default_percent_solar}
                    defaultValue={data.default_percent_solar}
                    unit="kWp"
                    onChange={(e, newValue) => handleChange("percent_solar", newValue)}
                  />
                </Grid>
              </Grid>
              <Grid className={classes.row} container alignItems="center">
                <Grid item xs={5}>
                  <Typography variant="body2">Installed new power assets</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Slider
                    color="#B7EBBC"
                    value={data.percent_new || data.default_percent_new}
                    defaultValue={data.default_percent_new}
                    unit="kW"
                    onChange={(e, newValue) => handleChange("percent_new", newValue)}
                  />
                </Grid>
              </Grid>
            </Grid>

            <PortfolioPowerRevenueCharges
              currentChartData={currentChartData}
              estimationChartData={estimationChartData}
              currentAddingPowerRevenues={currentAddingPowerRevenues}
              currentReducingUnrealizedRevenues={currentReducingUnrealizedRevenues}
              estimationAddingPowerRevenues={estimationAddingPowerRevenues}
              estimationReducingUnrealizedRevenues={estimationReducingUnrealizedRevenues}
            />
          </Grid>
        </ShaDowBox>
      ) : null}
    </>
  );
};
